import { Rect, measureText } from '@syncfusion/ej2-svg-base';
import { RectOption, appendChildElement, isCollide, getAnimationFunction } from '../../common/utils/helper';
import { Animation, isNullOrUndefined } from '@syncfusion/ej2-base';
import { getNodeColor } from '../model/sankey-theme';
export class SankeySeries {
    /**
     * Constructor.
     *
     * @param {Sankey} chart - Sankey chart instance.
     */
    constructor(chart) {
        this.dataLabelRects = [];
        this.chart = chart;
    }
    /**
     * Renders the Sankey diagram by computing layout, creating clip path, and drawing links, nodes, and data labels.
     *
     * @param {Sankey} chart - The Sankey chart instance used for rendering.
     * @returns {void}
     *
     * @private
     */
    render(chart) {
        if (!chart.svgObject) {
            return;
        }
        // Process data source if available, otherwise use links array
        const sankeyLinks = chart.links;
        if (!sankeyLinks.length) {
            return;
        }
        const clipRect = chart.initialClipRect;
        if (!clipRect) {
            return;
        }
        this.nodeWidth = chart.nodeStyle.width;
        this.nodePadding = chart.nodeStyle.padding;
        this.linkOpacity = chart.linkStyle.opacity;
        this.linkCurvature = chart.linkStyle.curvature;
        this.chart = chart;
        const nodeLevels = this.assignLevels(chart.nodeLayoutMap, sankeyLinks);
        this.computeLayout(chart.nodeLayoutMap, nodeLevels, clipRect);
        this.renderGroups(chart);
        // Create a clipPath for Sankey rendering and apply to groups so we can animate reveal
        const clipId = chart.element.id + '_sankey_clip';
        const clipPathElement = chart.renderer.createClipPath({ id: clipId });
        const clipRectOptions = new RectOption(clipId + '_rect', '', {}, 1, new Rect(clipRect.x - 10, clipRect.y - 10, clipRect.width + 20, chart.availableSize.height + 20));
        const clipRectElement = chart.renderer.drawRectangle(clipRectOptions);
        clipPathElement.appendChild(clipRectElement);
        appendChildElement(false, chart.svgObject, clipPathElement, false);
        this.renderLinks(chart, sankeyLinks, chart.nodeLayoutMap);
        this.renderNodes(chart, chart.nodeLayoutMap);
        this.renderDataLabels(chart, chart.nodeLayoutMap);
        // Animate the clip rect to reveal Sankey elements
        if (chart.animation.enable && chart.animateSeries && chart.animation.duration > 0) {
            this.animateClipRect(clipRectElement, clipRect, chart);
            chart.animateSeries = false;
        }
    }
    /**
     * Builds and returns a node layout map from the given links and user-defined node configuration.
     *
     * @param {SankeyLinkModel[]} links - Collection of Sankey links used to compute node in/out values.
     * @param {Sankey} chart - The Sankey chart instance that provides theme and user node definitions.
     * @returns {SankeyNodeLayout} returns node layout map for links and nodes.
     *
     * @private
     */
    buildNodes(links, chart) {
        const nodeLayouts = {};
        const colorPalette = getNodeColor(chart.theme);
        const userDefinedNodes = Array.isArray(chart.nodes) ? chart.nodes : [];
        let currentColorIndex = 0;
        const getNextColor = () => {
            const selectedColor = colorPalette[currentColorIndex % colorPalette.length];
            currentColorIndex++;
            return selectedColor;
        };
        const createNodeLayout = (nodeId, nodeColor, nodeLabel) => {
            return {
                id: nodeId,
                value: 0,
                inValue: 0,
                outValue: 0,
                level: 0,
                x: 0,
                y: 0,
                height: 0,
                outOffset: 0,
                inOffset: 0,
                color: nodeColor || getNextColor(),
                label: nodeLabel
            };
        };
        for (const userNode of userDefinedNodes) {
            if (!userNode || !userNode.id) {
                continue;
            }
            nodeLayouts[userNode.id] = createNodeLayout(userNode.id, userNode.color, userNode.label.text);
            if (!isNullOrUndefined(userNode.offset)) {
                nodeLayouts[userNode.id].offset = userNode.offset;
            }
        }
        const getOrCreateNode = (nodeId) => {
            if (!nodeLayouts[nodeId]) {
                nodeLayouts[nodeId] = createNodeLayout(nodeId);
            }
            return nodeLayouts[nodeId];
        };
        for (const link of links) {
            const sourceNodeLayout = getOrCreateNode(link.sourceId);
            const targetNodeLayout = getOrCreateNode(link.targetId);
            sourceNodeLayout.outValue += link.value;
            targetNodeLayout.inValue += link.value;
        }
        const nodeIds = Object.keys(nodeLayouts);
        for (let index = 0; index < nodeIds.length; index++) {
            const nodeLayout = nodeLayouts[nodeIds[index]];
            nodeLayout.value = nodeLayout.inValue > nodeLayout.outValue ? nodeLayout.inValue : nodeLayout.outValue;
        }
        return nodeLayouts;
    }
    /**
     * Assigns hierarchical levels to Sankey nodes using link direction and returns the total number of levels.
     *
     * @param {SankeyNodeLayout} nodes - Map of node id to its computed layout object.
     * @param {SankeyLinkModel[]} links - Collection of links used to compute node levels.
     * @returns {number} returns total number of levels.
     *
     * @private
     */
    assignLevels(nodes, links) {
        const nodeInDegreeMap = {};
        const nodeIds = Object.keys(nodes);
        for (let nodeIndex = 0; nodeIndex < nodeIds.length; nodeIndex++) {
            nodeInDegreeMap[nodeIds[nodeIndex]] = 0;
        }
        for (const link of links) {
            nodeInDegreeMap[link.targetId] = (nodeInDegreeMap[link.targetId] || 0) + 1;
        }
        const processingQueue = [];
        const allNodeIds = Object.keys(nodeInDegreeMap);
        for (let nodeIndex = 0; nodeIndex < allNodeIds.length; nodeIndex++) {
            const nodeId = allNodeIds[nodeIndex];
            if (nodeInDegreeMap[nodeId] === 0) {
                processingQueue.push(nodeId);
            }
        }
        const adjacencyList = {};
        for (const link of links) {
            if (!adjacencyList[link.sourceId]) {
                adjacencyList[link.sourceId] = [];
            }
            adjacencyList[link.sourceId].push(link.targetId);
        }
        let maximumLevel = 0;
        while (processingQueue.length) {
            const currentNodeId = processingQueue.shift();
            const currentLevel = nodes[currentNodeId] ? nodes[currentNodeId].level : 0;
            const childNodeIds = adjacencyList[currentNodeId] || [];
            for (let childIndex = 0; childIndex < childNodeIds.length; childIndex++) {
                const childNodeId = childNodeIds[childIndex];
                const childNode = nodes[childNodeId];
                if (childNode) {
                    childNode.level = childNode.level > (currentLevel + 1) ? childNode.level : (currentLevel + 1);
                    maximumLevel = maximumLevel > childNode.level ? maximumLevel : childNode.level;
                }
                nodeInDegreeMap[childNodeId] = (nodeInDegreeMap[childNodeId] || 0) - 1;
                if (nodeInDegreeMap[childNodeId] === 0) {
                    processingQueue.push(childNodeId);
                }
            }
        }
        return maximumLevel + 1;
    }
    /**
     * Computes node layout using vertical or horizontal strategy based on the chart orientation.
     *
     * @param {SankeyNodeLayout} nodes - Map of node id to its computed layout object.
     * @param {number} levelCount - Total number of levels used to distribute nodes.
     * @param {Rect} rect - The available clipping rectangle used for layout calculations.
     * @returns {void}
     */
    computeLayout(nodes, levelCount, rect) {
        const isVerticalLayout = this.chart && this.chart.orientation === 'Vertical';
        if (isVerticalLayout) {
            this.computeVerticalLayout(nodes, levelCount, rect);
        }
        else {
            this.computeHorizontalLayout(nodes, levelCount, rect);
        }
    }
    /**
     * Computes the horizontal layout positions and sizes for nodes across levels within the given rectangle.
     *
     * @param {SankeyNodeLayout} nodes - Map of node id to its computed layout object.
     * @param {number} levelCount - Total number of levels used to distribute nodes.
     * @param {Rect} rect - The available clipping rectangle used for layout calculations.
     * @returns {void}
     *
     * @private
     */
    computeHorizontalLayout(nodes, levelCount, rect) {
        const horizontalGapBetweenLevels = (levelCount > 1) ? (rect.width - this.nodeWidth) / (levelCount - 1) : 0;
        const nodesGroupedByLevel = {};
        const nodeIds = Object.keys(nodes);
        for (let nodeIndex = 0; nodeIndex < nodeIds.length; nodeIndex++) {
            const currentNodeLayout = nodes[nodeIds[nodeIndex]];
            if (currentNodeLayout) {
                const nodesAtCurrentLevel = nodesGroupedByLevel[currentNodeLayout.level] || [];
                nodesAtCurrentLevel.push(currentNodeLayout);
                nodesGroupedByLevel[currentNodeLayout.level] = nodesAtCurrentLevel;
            }
        }
        // Prepare global scaling: compute totals per level and use the largest-level total to derive a global scale.
        const levelTotals = [];
        let maxTotalValue = 0;
        let maxNodesInLevel = 0;
        for (let levelIndex = 0; levelIndex < levelCount; levelIndex++) {
            const nodesInCurrentLevel = nodesGroupedByLevel[levelIndex] || [];
            let totalValueInLevel = 0;
            for (let nodeIndex = 0; nodeIndex < nodesInCurrentLevel.length; nodeIndex++) {
                totalValueInLevel += nodesInCurrentLevel[nodeIndex].value;
            }
            levelTotals[levelIndex] = totalValueInLevel;
            if (totalValueInLevel > maxTotalValue) {
                maxTotalValue = totalValueInLevel;
            }
            if (nodesInCurrentLevel.length > maxNodesInLevel) {
                maxNodesInLevel = nodesInCurrentLevel.length;
            }
        }
        const maxTotalPadding = Math.max(maxNodesInLevel - 1, 0) * this.nodePadding;
        const globalValueToHeightScale = maxTotalValue > 0 ? (rect.height - maxTotalPadding) / maxTotalValue : 0;
        for (let levelIndex = 0; levelIndex < levelCount; levelIndex++) {
            const nodesInCurrentLevel = nodesGroupedByLevel[levelIndex] || [];
            const valueToHeightScale = globalValueToHeightScale;
            // compute heights for this level
            const nodeHeights = nodesInCurrentLevel.map((nodeLayout) => Math.max(1, nodeLayout.value * valueToHeightScale));
            const totalNodeHeights = nodeHeights.reduce((sum, height) => sum + height, 0);
            const occupiedHeight = totalNodeHeights + Math.max(0, nodeHeights.length - 1) * this.nodePadding;
            // center the level vertically inside rect
            let startY = rect.y;
            if (occupiedHeight < rect.height) {
                startY = rect.y + (rect.height - occupiedHeight) / 2;
            }
            let currentYPosition = startY;
            for (let nodeIndex = 0; nodeIndex < nodesInCurrentLevel.length; nodeIndex++) {
                const currentNodeLayout = nodesInCurrentLevel[nodeIndex];
                // Position nodes left-to-right normally; if RTL enabled, position right-to-left.
                if (this.chart && this.chart.enableRtl) {
                    currentNodeLayout.x = rect.x + rect.width - (levelIndex * horizontalGapBetweenLevels) - this.nodeWidth;
                }
                else {
                    currentNodeLayout.x = rect.x + levelIndex * horizontalGapBetweenLevels;
                }
                currentNodeLayout.height = nodeHeights[nodeIndex];
                currentNodeLayout.y = currentYPosition;
                currentNodeLayout.outOffset = 0;
                currentNodeLayout.inOffset = 0;
                // Apply user-specified offset (pixels or percentage string)
                if (!isNullOrUndefined(currentNodeLayout.offset)) {
                    currentNodeLayout.y += currentNodeLayout.offset;
                }
                currentYPosition += currentNodeLayout.height + this.nodePadding;
            }
        }
    }
    /**
     * Computes the vertical layout positions and sizes for nodes across levels within the given rectangle.
     *
     * @param {SankeyNodeLayout} nodes - Map of node id to its computed layout object.
     * @param {number} levelCount - Total number of levels used to distribute nodes.
     * @param {Rect} rect - The available clipping rectangle used for layout calculations.
     * @returns {void}
     *
     * @private
     */
    computeVerticalLayout(nodes, levelCount, rect) {
        const verticalGapBetweenLevels = (levelCount > 1) ? (rect.height - this.nodeWidth) / (levelCount - 1) : 0;
        const nodesGroupedByLevel = {};
        const nodeIds = Object.keys(nodes);
        // Group nodes by their level
        for (let nodeIndex = 0; nodeIndex < nodeIds.length; nodeIndex++) {
            const currentNodeLayout = nodes[nodeIds[nodeIndex]];
            if (currentNodeLayout) {
                const nodesAtCurrentLevel = nodesGroupedByLevel[currentNodeLayout.level] || [];
                nodesAtCurrentLevel.push(currentNodeLayout);
                nodesGroupedByLevel[currentNodeLayout.level] = nodesAtCurrentLevel;
            }
        }
        // Position nodes for each level (vertical orientation). Use global scaling similar to horizontal mode
        const levelTotalsVertical = [];
        let maxTotalValueVertical = 0;
        let maxNodesInLevelVertical = 0;
        for (let levelIndex = 0; levelIndex < levelCount; levelIndex++) {
            const nodesInCurrentLevel = nodesGroupedByLevel[levelIndex] || [];
            let totalValueInLevel = 0;
            for (let nodeIndex = 0; nodeIndex < nodesInCurrentLevel.length; nodeIndex++) {
                totalValueInLevel += nodesInCurrentLevel[nodeIndex].value;
            }
            levelTotalsVertical[levelIndex] = totalValueInLevel;
            if (totalValueInLevel > maxTotalValueVertical) {
                maxTotalValueVertical = totalValueInLevel;
            }
            if (nodesInCurrentLevel.length > maxNodesInLevelVertical) {
                maxNodesInLevelVertical = nodesInCurrentLevel.length;
            }
        }
        const maxTotalPaddingV = Math.max(maxNodesInLevelVertical - 1, 0) * this.nodePadding;
        const globalValueToWidthScale = maxTotalValueVertical > 0 ? (rect.width - maxTotalPaddingV) / maxTotalValueVertical : 0;
        for (let levelIndex = 0; levelIndex < levelCount; levelIndex++) {
            const nodesInCurrentLevel = nodesGroupedByLevel[levelIndex] || [];
            const levelY = rect.y + levelIndex * verticalGapBetweenLevels;
            // compute widths (stored in height) for this level
            const nodeSpanWidths = nodesInCurrentLevel.map((nodeLayout) => Math.max(1, nodeLayout.value * globalValueToWidthScale));
            const totalNodeWidths = nodeSpanWidths.reduce((sum, width) => sum + width, 0);
            const occupiedWidth = totalNodeWidths + Math.max(0, nodeSpanWidths.length - 1) * this.nodePadding;
            // center horizontally inside rect
            let startX = rect.x;
            if (occupiedWidth < rect.width) {
                startX = rect.x + (rect.width - occupiedWidth) / 2;
            }
            let currentXPosition = startX;
            for (let nodeIndex = 0; nodeIndex < nodesInCurrentLevel.length; nodeIndex++) {
                const currentNodeLayout = nodesInCurrentLevel[nodeIndex];
                currentNodeLayout.y = levelY;
                currentNodeLayout.x = currentXPosition;
                currentNodeLayout.height = nodeSpanWidths[nodeIndex];
                currentNodeLayout.outOffset = 0;
                currentNodeLayout.inOffset = 0;
                // Apply user-specified offset (pixels or percentage string) horizontally in vertical layout
                if (!isNullOrUndefined(currentNodeLayout.offset)) {
                    currentNodeLayout.x += currentNodeLayout.offset;
                }
                currentXPosition += currentNodeLayout.height + this.nodePadding;
            }
        }
    }
    /**
     * Creates and appends SVG groups for links, nodes, and labels with a clip-path applied.
     *
     * @param {Sankey} chart - The Sankey chart instance used to create and attach rendering groups.
     * @returns {void}
     */
    renderGroups(chart) {
        const chartElementId = chart.element.id;
        const linkGroupId = chartElementId + '_link_collection';
        const nodeGroupId = chartElementId + '_node_collection';
        const labelGroupId = chartElementId + '_label_collection';
        const clipId = chart.element.id + '_sankey_clip';
        const existingLinkGroup = document.getElementById(linkGroupId);
        if (existingLinkGroup && existingLinkGroup.parentNode) {
            existingLinkGroup.parentNode.removeChild(existingLinkGroup);
        }
        const existingNodeGroup = document.getElementById(nodeGroupId);
        if (existingNodeGroup && existingNodeGroup.parentNode) {
            existingNodeGroup.parentNode.removeChild(existingNodeGroup);
        }
        const existingLabelGroup = document.getElementById(labelGroupId);
        if (existingLabelGroup && existingLabelGroup.parentNode) {
            existingLabelGroup.parentNode.removeChild(existingLabelGroup);
        }
        const svgRenderer = chart.renderer;
        const linkCollectionGroup = svgRenderer.createGroup({ id: linkGroupId, 'clip-path': 'url(#' + clipId + ')' });
        const nodeCollectionGroup = svgRenderer.createGroup({ id: nodeGroupId, 'clip-path': 'url(#' + clipId + ')' });
        const labelCollectionGroup = svgRenderer.createGroup({ id: labelGroupId, 'clip-path': 'url(#' + clipId + ')' });
        // Append nodes first so links draw on top of nodes, then labels on top
        appendChildElement(false, chart.svgObject, nodeCollectionGroup, false);
        appendChildElement(false, chart.svgObject, linkCollectionGroup, false);
        appendChildElement(false, chart.svgObject, labelCollectionGroup, false);
    }
    /**
     * Renders Sankey data labels near nodes with collision handling and label rendering event support.
     *
     * @param {Sankey} chart - The Sankey chart instance used to render node labels.
     * @param {SankeyNodeLayout} nodes - Map of node id to its computed layout object.
     * @returns {void} returns void.
     */
    renderDataLabels(chart, nodes) {
        const labelSettings = chart.labelSettings;
        if (!(labelSettings).visible) {
            return;
        }
        let labelPadding;
        const labelFontSize = String((labelSettings).fontSize);
        const labelFontFamily = (labelSettings).fontFamily ? String((labelSettings).fontFamily) : '';
        const labelFontWeight = String((labelSettings).fontWeight);
        const labelFontStyle = String((labelSettings).fontStyle);
        const defaultLabelColor = String((labelSettings).color ||
            (chart.themeStyle && chart.themeStyle.datalabelFont && chart.themeStyle.datalabelFont.color) ||
            ((chart.theme && /Dark|HighContrast/i.test(String(chart.theme))) ? '#FFFFFF' : '#334155'));
        // Store for use in label rendering event
        const labelStyle = labelSettings;
        // Build totals for each node: prefer inValue (total incoming), else outValue
        const nodeTotals = {};
        const nodeIds = Object.keys(nodes);
        let maxLevel = -1;
        for (let nodeIndex = 0; nodeIndex < nodeIds.length; nodeIndex++) {
            const nodeId = nodeIds[nodeIndex];
            const nodeLayout = nodes[nodeId];
            nodeTotals[nodeId] = nodeLayout.inValue > 0 ? nodeLayout.inValue : nodeLayout.outValue;
            if (nodeLayout.level > maxLevel) {
                maxLevel = nodeLayout.level;
            }
        }
        this.dataLabelRects = [];
        // Ensure per-level label groups inside label collection
        const labelCollection = document.getElementById(chart.element.id + '_label_collection');
        labelCollection.setAttribute('aria-hidden', 'true');
        const ensureLevelLabelGroup = (level) => {
            let levelGroup = document.getElementById(`${chart.element.id}_label_level_${level}_g`);
            if (!levelGroup) {
                levelGroup = chart.renderer.createGroup({ id: `${chart.element.id}_label_level_${level}_g` });
                appendChildElement(false, labelCollection, levelGroup, false);
            }
            return levelGroup;
        };
        const isVertical = chart.orientation === 'Vertical';
        // Render labels next to nodes
        for (let nodeIndex = 0; nodeIndex < nodeIds.length; nodeIndex++) {
            const nodeLayout = nodes[nodeIds[nodeIndex]];
            const textElement = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            // Use custom label if available, otherwise use node ID
            const displayName = nodeLayout.label || nodeLayout.id;
            let labelText = displayName + ' ' + (nodeTotals[nodeLayout.id]);
            let labelColor = defaultLabelColor;
            let eventNode = null;
            let eventLink = null;
            const userNodes = Array.isArray(chart.nodes) ? chart.nodes : [];
            for (let userNodeIndex = 0; userNodeIndex < userNodes.length; userNodeIndex++) {
                const userNode = userNodes[userNodeIndex];
                if (userNode && userNode.id === nodeLayout.id) {
                    eventNode = userNode;
                    break;
                }
            }
            const userLinks = Array.isArray(chart.links) ? chart.links : [];
            for (let userLinkIndex = 0; userLinkIndex < userLinks.length; userLinkIndex++) {
                const userLink = userLinks[userLinkIndex];
                if (userLink && (userLink.sourceId === nodeLayout.id)) {
                    eventLink = userLink;
                    break;
                }
            }
            // Prefer node-level label settings over chart-level labelSettings
            const nodeLabelSettings = eventNode ? eventNode.label : null;
            labelPadding = (nodeLabelSettings && !isNullOrUndefined(nodeLabelSettings.padding)) ? nodeLabelSettings.padding :
                (labelSettings).padding;
            const labelRenderArgs = {
                text: labelText,
                node: eventNode,
                link: eventLink,
                labelStyle: labelStyle
            };
            const beforeLabelRendering = labelText;
            chart.trigger('labelRendering', labelRenderArgs);
            if (beforeLabelRendering !== labelRenderArgs.text) {
                nodeLayout.label = labelRenderArgs.text;
            }
            labelText = labelRenderArgs.text;
            labelColor = labelRenderArgs.labelStyle.color || defaultLabelColor;
            const levelGroup = ensureLevelLabelGroup(nodeLayout.level);
            const childIndex = levelGroup.childElementCount;
            textElement.setAttribute('id', `${chart.element.id}_label_level_${nodeLayout.level}_${childIndex}`);
            textElement.setAttribute('fill', labelColor);
            textElement.setAttribute('font-size', labelFontSize);
            if (labelFontFamily) {
                textElement.setAttribute('font-family', labelFontFamily);
            }
            if (labelFontWeight) {
                textElement.setAttribute('font-weight', labelFontWeight);
            }
            if (labelFontStyle) {
                textElement.setAttribute('font-style', labelFontStyle);
            }
            textElement.textContent = labelText;
            const fontModel = {
                size: labelFontSize,
                fontFamily: labelFontFamily,
                fontWeight: labelFontWeight,
                fontStyle: labelFontStyle
            };
            const textSize = measureText(labelText, fontModel, chart.themeStyle.datalabelFont);
            const labelWidth = textSize.width;
            const labelHeight = textSize.height;
            let xPos;
            let yPos;
            let rectX;
            let rectY;
            // Make isLastColumn available outside the branch so collision-shift can reuse the RTL-aware value
            let isLastColumn = false;
            if (isVertical) {
                isLastColumn = nodeLayout.level === maxLevel;
                const isLastRow = isLastColumn;
                yPos = isLastRow ? (nodeLayout.y - labelPadding) : (nodeLayout.y + this.nodeWidth + labelPadding);
                xPos = nodeLayout.x + nodeLayout.height / 2;
                const anchor = isLastRow ? 'end' : 'start';
                textElement.setAttribute('x', String(xPos));
                textElement.setAttribute('y', String(yPos));
                textElement.setAttribute('dominant-baseline', anchor === 'end' ? 'baseline' : 'hanging');
                textElement.setAttribute('text-anchor', 'middle');
                if (isLastRow) {
                    rectY = yPos - labelHeight;
                }
                else {
                    rectY = yPos;
                }
                rectX = xPos - labelWidth / 2;
            }
            else {
                isLastColumn = isVertical ? (nodeLayout.level === maxLevel) : ((chart.enableRtl) ? (nodeLayout.level === 0)
                    : (nodeLayout.level === maxLevel));
                xPos = isLastColumn ? (nodeLayout.x - labelPadding) : (nodeLayout.x + this.nodeWidth + labelPadding);
                yPos = nodeLayout.y + nodeLayout.height / 2;
                const anchor = isLastColumn ? 'end' : 'start';
                // Flip start/end anchor semantics for RTL so text alignment matches direction
                const anchorText = (chart && chart.enableRtl) ? (anchor === 'end' ? 'start' : (anchor === 'start' ? 'end' : anchor)) : anchor;
                textElement.setAttribute('x', String(xPos));
                textElement.setAttribute('y', String(yPos));
                textElement.setAttribute('dominant-baseline', 'middle');
                textElement.setAttribute('text-anchor', anchorText);
                if (isLastColumn) {
                    rectX = xPos - labelWidth;
                }
                else {
                    rectX = xPos;
                }
                rectY = yPos - labelHeight / 2;
            }
            const labelRect = new Rect(rectX, rectY, labelWidth, labelHeight);
            let tries = 0;
            const maxTries = 6;
            const step = 10;
            while (isCollide(labelRect, this.dataLabelRects, { x: 0, y: 0, width: 0, height: 0 }) && tries < maxTries) {
                tries++;
                if (isVertical) {
                    labelRect.y += (nodeLayout.level === maxLevel) ? -step : step;
                    yPos += (nodeLayout.level === maxLevel) ? -step : step;
                    textElement.setAttribute('y', String(yPos));
                }
                else {
                    // use the RTL-aware isLastColumn (declared above) when shifting to avoid LTR-only logic
                    labelRect.x += isLastColumn ? -step : step;
                    xPos += isLastColumn ? -step : step;
                    textElement.setAttribute('x', String(xPos));
                }
            }
            if (!isCollide(labelRect, this.dataLabelRects, { x: 0, y: 0, width: 0, height: 0 })) {
                levelGroup.appendChild(textElement);
                this.dataLabelRects.push(labelRect);
            }
        }
    }
    /**
     * Renders Sankey node rectangles into level-based SVG groups using configured node styles and rendering events.
     *
     * @param {Sankey} chart - The Sankey chart instance used for rendering nodes.
     * @param {SankeyNodeLayout} nodes - Map of node id to its computed layout object.
     * @returns {void} returns void.
     *
     * @private
     */
    renderNodes(chart, nodes) {
        const nodeCollectionGroup = document.getElementById(chart.element.id + '_node_collection');
        if (!nodeCollectionGroup) {
            return;
        }
        const nodeStyle = chart.nodeStyle ? chart.nodeStyle : {};
        const nodeStroke = nodeStyle.stroke;
        const nodeStrokeWidth = typeof nodeStyle.strokeWidth === 'number' ? nodeStyle.strokeWidth : 1;
        const nodeOpacity = typeof nodeStyle.opacity === 'number' ? nodeStyle.opacity : 1;
        const nodeFill = nodeStyle.fill;
        const isVertical = chart.orientation === 'Vertical';
        const nodeIds = Object.keys(nodes);
        for (let nodeIndex = 0; nodeIndex < nodeIds.length; nodeIndex++) {
            const currentNodeLayout = nodes[nodeIds[nodeIndex]];
            // ensure level group exists
            const levelGroupId = `${chart.element.id}_node_level_${currentNodeLayout.level}_g`;
            let levelGroup = document.getElementById(levelGroupId);
            if (!levelGroup) {
                levelGroup = chart.renderer.createGroup({ id: levelGroupId });
                appendChildElement(false, nodeCollectionGroup, levelGroup, false);
            }
            let fillColor = nodeFill || currentNodeLayout.color;
            // Trigger nodeRendering event
            let eventNode = null;
            const userNodes = chart.nodes || [];
            for (let userNodeIndex = 0; userNodeIndex < userNodes.length; userNodeIndex++) {
                const userNode = userNodes[userNodeIndex];
                if (userNode && userNode.id === currentNodeLayout.id) {
                    eventNode = userNode;
                    break;
                }
            }
            const nodeRenderArgs = {
                node: eventNode,
                fill: fillColor
            };
            chart.trigger('nodeRendering', nodeRenderArgs);
            fillColor = nodeRenderArgs.fill;
            let nodeWidth = this.nodeWidth;
            let nodeHeight = currentNodeLayout.height;
            if (isVertical) {
                nodeWidth = currentNodeLayout.height;
                nodeHeight = this.nodeWidth;
            }
            const nodeRectOptions = new RectOption('', fillColor, { color: nodeStroke, width: nodeStrokeWidth }, 1, new Rect(currentNodeLayout.x, currentNodeLayout.y, nodeWidth, nodeHeight));
            const nodeRectElement = chart.renderer.drawRectangle(nodeRectOptions);
            // assign level-based sequential id: <chartId>_node_level_<level>_<index>
            const levelChildCount = levelGroup.childElementCount;
            const nodeElementId = `${chart.element.id}_node_level_${currentNodeLayout.level}_${levelChildCount}`;
            nodeRectElement.setAttribute('id', nodeElementId);
            nodeRectElement.setAttribute('aria-label', currentNodeLayout.id);
            nodeRectElement.setAttribute('role', 'region');
            nodeRectElement.setAttribute('tabindex', '-1'); // Readable, non-focusable
            nodeRectElement.setAttribute('aria-hidden', 'false');
            // Apply opacity
            if (nodeOpacity < 1) {
                nodeRectElement.setAttribute('opacity', nodeOpacity.toString());
            }
            appendChildElement(false, levelGroup, nodeRectElement, false);
        }
    }
    /**
     * Renders Sankey links into the link collection group with ordering, styling, and link rendering event support.
     *
     * @param {Sankey} chart - The Sankey chart instance used for rendering links.
     * @param {SankeyLinkModel[]} links - Collection of link models used to create rendered link paths.
     * @param { SankeyNodeLayout } nodes - Map of node id to its computed layout object.
     * @returns {void}
     *
     * @private
     */
    renderLinks(chart, links, nodes) {
        const linkCollectionGroup = document.getElementById(chart.element.id + '_link_collection');
        if (!linkCollectionGroup) {
            return;
        }
        const linkStyle = chart.linkStyle;
        const effectiveLinkOpacity = typeof linkStyle.opacity === 'number' ? linkStyle.opacity : this.linkOpacity;
        const isVertical = chart.orientation === 'Vertical';
        const gapBetweenLevels = isVertical ?
            this.getVerticalGapBetweenLevels(nodes, chart.initialClipRect) :
            this.getHorizontalGapBetweenLevels(nodes, chart.initialClipRect);
        // Sort links to produce tidy stacking
        const sortedLinks = links.slice().sort((linkA, linkB) => {
            const sourceA = nodes[linkA.sourceId];
            const sourceB = nodes[linkB.sourceId];
            const targetA = nodes[linkA.targetId];
            const targetB = nodes[linkB.targetId];
            if (isVertical) {
                const levelA = sourceA ? sourceA.level : 0;
                const levelB = sourceB ? sourceB.level : 0;
                if (levelA !== levelB) {
                    return levelA - levelB;
                }
                const sourceXDiff = (sourceA ? sourceA.x : 0) - (sourceB ? sourceB.x : 0);
                if (sourceXDiff !== 0) {
                    return sourceXDiff;
                }
                return (targetA ? targetA.x : 0) - (targetB ? targetB.x : 0);
            }
            else {
                const isrtl = !!(this.chart && this.chart.enableRtl);
                const sourceXA = sourceA ? sourceA.x : 0;
                const sourceXB = sourceB ? sourceB.x : 0;
                const primary = isrtl ? (sourceXB - sourceXA) : (sourceXA - sourceXB);
                if (primary !== 0) {
                    return primary;
                }
                const targetYA = targetA ? targetA.y : 0;
                const targetYB = targetB ? targetB.y : 0;
                return targetYA - targetYB;
            }
        });
        const userLinks = chart.links || [];
        const colorType = chart.linkStyle && (chart.linkStyle).colorType;
        for (let linkIndex = 0; linkIndex < sortedLinks.length; linkIndex++) {
            const currentLink = sortedLinks[linkIndex];
            const sourceNodeLayout = nodes[currentLink.sourceId];
            const targetNodeLayout = nodes[currentLink.targetId];
            if (!sourceNodeLayout || !targetNodeLayout) {
                continue;
            }
            // Find matching user link for event payload (if any)
            let eventLink = null;
            for (let j = 0; j < userLinks.length; j++) {
                const ul = userLinks[j];
                if (ul && ul.sourceId === currentLink.sourceId && ul.targetId === currentLink.targetId) {
                    eventLink = ul;
                    break;
                }
            }
            // Determine default link color based on configured colorType: Blend, Source, or Target
            let defaultLinkColor;
            if (colorType === 'Target') {
                defaultLinkColor = targetNodeLayout.color;
            }
            else if (colorType === 'Blend') {
                // Use gradient id
                const gradId = this.getOrCreateLinkGradient(chart, sourceNodeLayout.color, targetNodeLayout.color, 
                /* isHorizontal */ !isVertical, 
                /* isRtl */ !!chart.enableRtl, 
                /* index */ linkIndex, currentLink.sourceId, currentLink.targetId);
                defaultLinkColor = gradId; // renderer will convert to url(#id)
            }
            else {
                // 'Source' or fallback
                defaultLinkColor = sourceNodeLayout.color;
            }
            const linkRenderArgs = {
                // Prefer the user-facing link object; fall back to model if needed
                link: (eventLink) || currentLink,
                fill: defaultLinkColor
            };
            chart.trigger('linkRendering', linkRenderArgs);
            const finalFill = (linkRenderArgs && linkRenderArgs.fill) ? linkRenderArgs.fill : defaultLinkColor;
            if (isVertical) {
                this.renderVerticalLink(linkCollectionGroup, currentLink, sourceNodeLayout, targetNodeLayout, gapBetweenLevels, effectiveLinkOpacity, linkIndex, finalFill);
            }
            else {
                this.renderHorizontalLink(linkCollectionGroup, currentLink, sourceNodeLayout, targetNodeLayout, gapBetweenLevels, effectiveLinkOpacity, linkIndex, finalFill);
            }
        }
    }
    /**
     * Renders a horizontal Sankey link path between source and target nodes and appends it into a level-based SVG group.
     *
     * @param {Element} linkCollectionGroup - The parent SVG group that holds all rendered link groups.
     * @param {SankeyLinkModel} currentLink - The current link model used to compute the rendered path and metadata.
     * @param {SankeyNodeLayout} sourceNode - The source node layout used to compute link start position and thickness.
     * @param {SankeyNodeLayout} targetNode - The target node layout used to compute link end position and thickness.
     * @param {number} gapBetweenLevels - The horizontal gap between node levels used for bezier curvature calculation.
     * @param {number} linkOpacity - The opacity value applied to the rendered link path.
     * @param {number} index - The link index used to generate a stable link key attribute.
     * @param {string} fill - The fill colcor based on the selected theme style.
     * @returns {void}
     *
     * @private
     */
    renderHorizontalLink(linkCollectionGroup, currentLink, sourceNode, targetNode, gapBetweenLevels, linkOpacity, index, fill) {
        const outgoingLinkHeight = sourceNode.value > 0 ? (currentLink.value / sourceNode.value) * sourceNode.height : 0;
        const incomingLinkHeight = targetNode.value > 0 ? (currentLink.value / targetNode.value) * targetNode.height : 0;
        const linkHeight = Math.max(0, Math.min(outgoingLinkHeight, incomingLinkHeight));
        const isRtl = this.chart && this.chart.enableRtl;
        // In RTL mode: source connects from left, target connects to right
        // In LTR mode: source connects from right, target connects to left
        const sourceX = isRtl ? sourceNode.x : (sourceNode.x + this.nodeWidth);
        const sourceY = sourceNode.y + sourceNode.outOffset + linkHeight / 2;
        const targetX = isRtl ? (targetNode.x + this.nodeWidth) : targetNode.x;
        const targetY = targetNode.y + targetNode.inOffset + linkHeight / 2;
        sourceNode.outOffset += linkHeight;
        targetNode.inOffset += linkHeight;
        const curvatureOffset = gapBetweenLevels * this.linkCurvature;
        const pointX = isRtl ? (sourceX - curvatureOffset) : (sourceX + curvatureOffset);
        const pointY = isRtl ? (targetX + curvatureOffset) : (targetX - curvatureOffset);
        // Build closed ribbon path (top edge -> target top -> target bottom -> source bottom -> close)
        const sourceTop = (sourceY - linkHeight / 2) || 0;
        const sourceBottom = (sourceY + linkHeight / 2) || 0;
        const targetTop = (targetY - linkHeight / 2) || 0;
        const targetBottom = (targetY + linkHeight / 2) || 0;
        const pathD = 'M ' + sourceX + ' ' + sourceTop +
            ' C ' + pointX + ' ' + sourceTop + ', ' + pointY + ' ' + targetTop + ', ' + targetX + ' ' + targetTop +
            ' L ' + targetX + ' ' + targetBottom +
            ' C ' + pointY + ' ' + targetBottom + ', ' + pointX + ' ' + sourceBottom + ', ' + sourceX + ' ' + sourceBottom +
            ' Z';
        const linkPathElement = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        const levelGroupId = `${this.chart.element.id}_link_level_${sourceNode.level}_g`;
        let levelGroup = document.getElementById(levelGroupId);
        if (!levelGroup) {
            levelGroup = this.chart.renderer.createGroup({ id: levelGroupId });
            appendChildElement(false, linkCollectionGroup, levelGroup, false);
        }
        const levelChildCount = levelGroup.childElementCount;
        const generatedLinkId = `${this.chart.element.id}_link_level_${sourceNode.level}_${levelChildCount}`;
        linkPathElement.setAttribute('id', generatedLinkId);
        linkPathElement.setAttribute('d', pathD);
        // Use gradient URL if grad id provided, otherwise color fill
        if (fill && fill.indexOf('gradient-') === 0) {
            linkPathElement.setAttribute('fill', `url(#${fill})`);
            linkPathElement.setAttribute('stroke', 'none');
            linkPathElement.setAttribute('stroke-width', '0');
        }
        else if (fill && fill.indexOf('url(') === 0) {
            linkPathElement.setAttribute('fill', fill);
            linkPathElement.setAttribute('stroke', 'none');
            linkPathElement.setAttribute('stroke-width', '0');
        }
        else {
            linkPathElement.setAttribute('fill', fill || sourceNode.color);
            linkPathElement.setAttribute('stroke', 'none');
            linkPathElement.setAttribute('stroke-width', '0');
        }
        linkPathElement.setAttribute('opacity', linkOpacity ? linkOpacity.toString() : '1');
        linkPathElement.setAttribute('data-source', currentLink.sourceId);
        linkPathElement.setAttribute('data-target', currentLink.targetId);
        linkPathElement.setAttribute('data-value', currentLink.value.toString());
        linkPathElement.setAttribute('data-link-key', `${currentLink.sourceId}__${currentLink.targetId}__${index}`);
        linkPathElement.setAttribute('role', 'link');
        const linkDescription = this.chart.accessibility.accessibilityDescription ||
            `From ${currentLink.sourceId} to ${currentLink.targetId}: Value ${currentLink.value}`;
        linkPathElement.setAttribute('aria-label', linkDescription);
        linkPathElement.setAttribute('tabindex', '-1');
        appendChildElement(false, levelGroup, linkPathElement, false);
    }
    /**
     * Renders a vertical Sankey link as a filled ribbon path between source and target nodes and appends it into a level-based SVG group.
     *
     * @param {Element} linkCollectionGroup - The parent SVG group that holds all rendered link groups.
     * @param {SankeyLinkModel} currentLink - The current link model used to compute the rendered ribbon path and metadata.
     * @param {SankeyNodeLayout} sourceNode - The source node layout used to compute ribbon start position and thickness.
     * @param {SankeyNodeLayout} targetNode - The target node layout used to compute ribbon end position and thickness.
     * @param {number} gapBetweenLevels - The vertical gap between node levels used for curvature calculation.
     * @param {number} linkOpacity - The opacity value applied to the rendered ribbon path.
     * @param {number} index - The link index used to generate a stable link key attribute.
     * @param {string} fill - The fill color of link based on theme color selected.
     * @returns {void}
     *
     * @private
     */
    renderVerticalLink(linkCollectionGroup, currentLink, sourceNode, targetNode, gapBetweenLevels, linkOpacity, index, fill) {
        const outBandWidth = sourceNode.value > 0 ? (currentLink.value / sourceNode.value) * sourceNode.height : 0;
        const inBandWidth = targetNode.value > 0 ? (currentLink.value / targetNode.value) * targetNode.height : 0;
        const linkBaseWidt = Math.min(outBandWidth, inBandWidth);
        // Clamp to remaining band on each node so the last link closes cleanly
        const sourceRemainingWidth = Math.max(0, sourceNode.height - sourceNode.outOffset);
        const targetRemainingWidth = Math.max(0, targetNode.height - targetNode.inOffset);
        let linkWidth = Math.max(0, Math.min(linkBaseWidt, sourceRemainingWidth, targetRemainingWidth));
        if (sourceRemainingWidth - linkWidth < 0.5 || targetRemainingWidth - linkWidth < 0.5) {
            linkWidth = Math.min(sourceRemainingWidth, targetRemainingWidth);
        }
        // Left/right edges on source and target nodes (in vertical layout, node.height represents horizontal span)
        const sourceLeftX = sourceNode.x + sourceNode.outOffset;
        const sourceRightX = sourceLeftX + linkWidth;
        const targetLeftX = targetNode.x + targetNode.inOffset;
        const targetRightX = targetLeftX + linkWidth;
        // Y coordinates: from bottom of source row to top of target row
        const y1 = sourceNode.y + this.nodeWidth; // bottom of source node (row thickness = nodeWidth)
        const y2 = targetNode.y; // top of target node
        // Update offsets AFTER we compute this band
        sourceNode.outOffset += linkWidth;
        targetNode.inOffset += linkWidth;
        // Curvature clamped to half the gap
        const rawCurv = gapBetweenLevels * this.linkCurvature;
        const vGap = Math.max(0, y2 - y1);
        const curve = Math.min(rawCurv, Math.max(0, vGap / 2 - 1));
        const c1Y = y1 + curve;
        const c2Y = y2 - curve;
        // Build closed ribbon path
        const pathD = 'M ' + sourceLeftX + ' ' + y1 +
            ' C ' + sourceLeftX + ' ' + c1Y + ', ' + targetLeftX + ' ' + c2Y + ', ' + targetLeftX + ' ' + y2 +
            ' L ' + targetRightX + ' ' + y2 +
            ' C ' + targetRightX + ' ' + c2Y + ', ' + sourceRightX + ' ' + c1Y + ', ' + sourceRightX + ' ' + y1 +
            ' Z';
        const linkPathElement = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        const levelGroupId = `${this.chart.element.id}_link_level_${sourceNode.level}_g`;
        let levelGroup = document.getElementById(levelGroupId);
        if (!levelGroup) {
            levelGroup = this.chart.renderer.createGroup({ id: levelGroupId });
            appendChildElement(false, linkCollectionGroup, levelGroup, false);
        }
        const levelChildCount = levelGroup.childElementCount;
        const generatedLinkId = `${this.chart.element.id}_link_level_${sourceNode.level}_${levelChildCount}`;
        linkPathElement.setAttribute('id', generatedLinkId);
        linkPathElement.setAttribute('d', pathD);
        // Use gradient URL if grad id provided, otherwise color fill
        if (fill && fill.indexOf('gradient-') === 0) {
            linkPathElement.setAttribute('fill', `url(#${fill})`);
        }
        else if (fill && fill.indexOf('url(') === 0) {
            linkPathElement.setAttribute('fill', fill);
        }
        else {
            linkPathElement.setAttribute('fill', fill || sourceNode.color);
        }
        linkPathElement.setAttribute('opacity', linkOpacity.toString());
        linkPathElement.setAttribute('data-source', currentLink.sourceId);
        linkPathElement.setAttribute('data-target', currentLink.targetId);
        linkPathElement.setAttribute('data-value', currentLink.value.toString());
        linkPathElement.setAttribute('data-link-key', `${currentLink.sourceId}__${currentLink.targetId}__${index}`);
        linkPathElement.setAttribute('role', 'link');
        const linkDescription = this.chart.accessibility.accessibilityDescription ||
            `From ${currentLink.sourceId} to ${currentLink.targetId}: Value ${currentLink.value}`;
        linkPathElement.setAttribute('aria-label', linkDescription);
        linkPathElement.setAttribute('tabindex', '-1');
        appendChildElement(false, levelGroup, linkPathElement, false);
    }
    /**
     * Calculates the horizontal gap between node levels based on the minimum and maximum levels present.
     *
     * @param { SankeyNodeLayout } nodes - Map of node id to its computed layout object.
     * @param {Rect} rect - The available clipping rectangle used for gap calculation.
     * @returns {number} returns gap in number
     *
     * @private
     */
    getHorizontalGapBetweenLevels(nodes, rect) {
        let minimumLevel = 9007199254740991; // Number.MAX_SAFE_INTEGER alternative
        let maximumLevel = -1;
        const nodeIds = Object.keys(nodes);
        for (let nodeIndex = 0; nodeIndex < nodeIds.length; nodeIndex++) {
            const currentNodeLayout = nodes[nodeIds[nodeIndex]];
            minimumLevel = minimumLevel < currentNodeLayout.level ? minimumLevel : currentNodeLayout.level;
            maximumLevel = maximumLevel > currentNodeLayout.level ? maximumLevel : currentNodeLayout.level;
        }
        const totalLevels = (maximumLevel - minimumLevel > 0 ? (maximumLevel - minimumLevel) : 0) + 1;
        return (totalLevels > 1) ? (rect.width - this.nodeWidth) / (totalLevels - 1) : rect.width;
    }
    /**
     * Calculates the vertical gap between node levels based on the minimum and maximum levels present.
     *
     * @param {SankeyNodeLayout} nodes - Map of node id to its computed layout object.
     * @param {Rect} rect - The available clipping rectangle used for gap calculation.
     * @returns {number} returns gap in number
     *
     * @private
     */
    getVerticalGapBetweenLevels(nodes, rect) {
        let minimumLevel = 9007199254740991; // Number.MAX_SAFE_INTEGER alternative
        let maximumLevel = -1;
        const nodeIds = Object.keys(nodes);
        for (let nodeIndex = 0; nodeIndex < nodeIds.length; nodeIndex++) {
            const currentNodeLayout = nodes[nodeIds[nodeIndex]];
            minimumLevel = minimumLevel < currentNodeLayout.level ? minimumLevel : currentNodeLayout.level;
            maximumLevel = maximumLevel > currentNodeLayout.level ? maximumLevel : currentNodeLayout.level;
        }
        const totalLevels = (maximumLevel - minimumLevel > 0 ? (maximumLevel - minimumLevel) : 0) + 1;
        return (totalLevels > 1) ? (rect.height - this.nodeWidth) / (totalLevels - 1) : rect.height;
    }
    /**
     * Sanitizes a string value into a DOM-safe identifier by replacing unsupported characters with underscores.
     *
     * @param {string} text - The input text to be sanitized for DOM id usage.
     * @returns {string} return string of Id.
     */
    sanitizeId(text) {
        return text.replace(/[^a-zA-Z0-9_]/g, '_');
    }
    /**
     * Creates (or reuses) an SVG <linearGradient> definition for a link that transitions from startColor to endColor.
     *
     * @param {Sankey} chart - The Sankey chart instance used to access the SVG root and renderer.
     * @param {string} startColor - The gradient start color applied at 0% offset.
     * @param {string} endColor - The gradient end color applied at 100% offset.
     * @param {boolean} isHorizontal - Indicates whether the link gradient should be applied horizontally.
     * @param {boolean} isRtl - Indicates whether the gradient direction should be reversed for RTL layouts.
     * @param {number} index - The link index used to generate a stable unique gradient id.
     * @param {string} sourceId - The source node id used to generate a stable unique gradient id.
     * @param {string} targetId - The target node id used to generate a stable unique gradient id.
     * @returns {string} The id of the created/reused gradient, or a fallback solid color when SVG is unavailable.
     *
     * @private
     */
    getOrCreateLinkGradient(chart, startColor, endColor, isHorizontal, isRtl, index, sourceId, targetId) {
        const svgRootElement = chart.svgObject;
        if (!svgRootElement) {
            return startColor || endColor || '#000000';
        }
        // Use renderer to create defs to match existing patterns in the codebase
        let defsElement = svgRootElement.querySelector('defs');
        if (!defsElement) {
            defsElement = chart.renderer.createDefs();
            svgRootElement.append(defsElement);
        }
        const gradientId = `gradient-${this.sanitizeId(chart.element.id)}_` +
            `${this.sanitizeId(String(sourceId))}_` +
            `${this.sanitizeId(String(targetId))}_` +
            `${index}`;
        if (defsElement.querySelector(`#${gradientId}`)) {
            return gradientId;
        }
        const svgNamespaceUri = 'http://www.w3.org/2000/svg';
        const linearGradientElement = document.createElementNS(svgNamespaceUri, 'linearGradient');
        linearGradientElement.setAttribute('id', gradientId);
        if (isHorizontal) {
            if (isRtl) {
                linearGradientElement.setAttribute('x1', '100%');
                linearGradientElement.setAttribute('y1', '0%');
                linearGradientElement.setAttribute('x2', '0%');
                linearGradientElement.setAttribute('y2', '0%');
            }
            else {
                linearGradientElement.setAttribute('x1', '0%');
                linearGradientElement.setAttribute('y1', '0%');
                linearGradientElement.setAttribute('x2', '100%');
                linearGradientElement.setAttribute('y2', '0%');
            }
        }
        else {
            linearGradientElement.setAttribute('x1', '0%');
            linearGradientElement.setAttribute('y1', '0%');
            linearGradientElement.setAttribute('x2', '0%');
            linearGradientElement.setAttribute('y2', '100%');
        }
        const startStopElement = document.createElementNS(svgNamespaceUri, 'stop');
        startStopElement.setAttribute('offset', '0%');
        startStopElement.setAttribute('stop-color', startColor);
        startStopElement.setAttribute('stop-opacity', '1');
        const endStopElement = document.createElementNS(svgNamespaceUri, 'stop');
        endStopElement.setAttribute('offset', '100%');
        endStopElement.setAttribute('stop-color', endColor);
        endStopElement.setAttribute('stop-opacity', '1');
        linearGradientElement.appendChild(startStopElement);
        linearGradientElement.appendChild(endStopElement);
        defsElement.appendChild(linearGradientElement);
        return gradientId;
    }
    /**
     * To destroy the series.
     *
     * @returns {void}
     * @private
     */
    destroy() {
        /**
         * Destroy method performed here.
         */
    }
    /**
     * Get module name.
     *
     * @returns {string} - Returns the module name.
     */
    getModuleName() {
        return 'SankeySeries';
    }
    /**
     * Animate the clip rect for Sankey reveal.
     *
     * @param {HTMLElement} clipElement - The clip rectangle element used to reveal Sankey elements.
     * @param {Rect} rect - The clipping rectangle bounds used to compute the reveal transform.
     * @param {Sankey} chart - The Sankey chart instance used to resolve animation settings and orientation.
     * @returns {void}
     */
    animateClipRect(clipElement, rect, chart) {
        const animationEffect = getAnimationFunction('Linear');
        const duration = (chart.animation).duration;
        clipElement.style.visibility = 'hidden';
        new Animation({}).animate(clipElement, {
            duration: duration,
            delay: 0,
            progress: (args) => {
                clipElement.style.visibility = 'visible';
                if (chart.orientation === 'Vertical') {
                    const animatedValue = animationEffect(args.timeStamp, 0, rect.height, args.duration);
                    const xAnchor = rect.x + rect.width / 2;
                    const yAnchor = rect.y + rect.height; // anchor at bottom
                    clipElement.setAttribute('transform', 'translate(' + xAnchor + ' ' + yAnchor + ') scale(1,' + (animatedValue / rect.height) + ') translate(' + (-xAnchor) + ' ' + (-yAnchor) + ')');
                }
                else {
                    const animatedValue = animationEffect(args.timeStamp, 0, rect.width, args.duration);
                    // For RTL, anchor at right edge so reveal runs right->left
                    const isRtl = chart && chart.enableRtl;
                    const xAnchor = isRtl ? (rect.x + rect.width) : rect.x;
                    const yAnchor = rect.y + rect.height / 2;
                    const scaleX = (animatedValue / rect.width);
                    // when anchoring at right we still scale from 0->1; transform sequence centers on anchor
                    clipElement.setAttribute('transform', 'translate(' + xAnchor + ' ' + yAnchor + ') scale(' + (scaleX) + ',1) translate(' + (-xAnchor) + ' ' + (-yAnchor) + ')');
                }
            },
            end: () => {
                clipElement.setAttribute('transform', 'translate(0,0)');
                if ((chart).trigger) {
                    chart.trigger('animationComplete', {});
                }
            }
        });
    }
}
